import { useAuthStore } from "@features/auth/store/authStore";
import { Button } from "@shared/components/ui/Button";
import { ImageUploader } from "@shared/components/ui/ImageUploader";
import { MediaService } from "@shared/services/media.service";
import { MediaFile } from "@shared/types/media.types";
import { UserProfile } from "@shared/types/user";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../../config/firebase";
import theme from "../../../shared/utils/themeColors";
import OnboardingLayout from "../components/OnboardingLayout";
import { useOnboarding } from "../hooks/useOnboarding";
import { OnboardingStep } from "../types";

/**
 * Third step of onboarding - Profile information
 */
const Onboarding03Page: React.FC = () => {
  const { profileInfo, loading, error, saveProfileInfo, setError } =
    useOnboarding();

  const navigate = useNavigate();
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<MediaFile | null>(null);

  // Get user from auth store if available
  const authUser = useAuthStore((state) => state.user);

  // Initialize form state with existing data or defaults
  const [formData, setFormData] = useState<Partial<UserProfile>>({
    name: profileInfo?.name || authUser?.profile?.name || "",
    role: profileInfo?.role || authUser?.profile?.role || "",
    summary: profileInfo?.summary || "",
    aboutMe: profileInfo?.aboutMe || "",
    location: profileInfo?.location || "",
    imageUrl: profileInfo?.imageUrl || "",
  });

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle image selection
  const handleImageSelected = async (file: MediaFile) => {
    setSelectedImage(file);
    setFormData((prev) => ({ ...prev, imageUrl: file.preview }));
  };

  // Handle image removal
  const handleImageRemoved = () => {
    setSelectedImage(null);
    setFormData((prev) => ({ ...prev, imageUrl: "" }));
  };

  // Upload image to Firebase Storage
  const uploadProfileImage = async (): Promise<string | undefined> => {
    if (!selectedImage || !auth.currentUser) return undefined;

    try {
      setImageLoading(true);
      const imageUrl = await MediaService.uploadMedia(
        auth.currentUser.uid,
        selectedImage,
        {},
        (progress) => {
          // Update progress in the UI if needed
          console.log(`Upload progress: ${progress.progress}%`);
        }
      );
      return imageUrl;
    } catch (error) {
      console.error("Error uploading profile image:", error);
      setError("Error uploading profile image. Please try again.");
      return undefined;
    } finally {
      setImageLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name) {
      setError("Name is required");
      return;
    }

    try {
      // Upload image if selected
      let updatedFormData = { ...formData };

      if (selectedImage) {
        const imageUrl = await uploadProfileImage();
        if (imageUrl) {
          updatedFormData.imageUrl = imageUrl;
        }
      }

      // Save profile info
      saveProfileInfo(updatedFormData);
    } catch (error) {
      console.error("Error saving profile:", error);
      setError("Error saving profile. Please try again.");
    }
  };

  return (
    <OnboardingLayout currentStep={OnboardingStep.PROFILE_INFO}>
      <h1 className={`text-3xl ${theme.text.primary} mb-6`}>
        Your Profile Information
      </h1>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Image Upload */}
        <div className="flex flex-col items-center">
          <div className="mb-2">
            <label
              className={`block text-sm font-medium ${theme.text.primary} mb-2 text-center`}
            >
              Profile Image
            </label>
            <div className="w-32 h-32 mx-auto">
              <ImageUploader
                className="w-32 h-32"
                imageUrl={formData.imageUrl}
                onImageSelected={handleImageSelected}
                onImageRemoved={handleImageRemoved}
                disabled={loading || imageLoading}
                isUploading={imageLoading}
                loadingPosition="bottom"
              />
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor="name"
            className={`block text-sm font-medium ${theme.text.primary} mb-1`}
          >
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
            placeholder="Enter your full name"
            required
          />
        </div>

        <div>
          <label
            htmlFor="role"
            className={`block text-sm font-medium ${theme.text.primary} mb-1`}
          >
            Your Role
          </label>
          <input
            type="text"
            id="role"
            name="role"
            value={formData.role || ""}
            onChange={handleChange}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
            placeholder="E.g., Sustainability Manager, CEO, Director"
          />
        </div>

        <div>
          <label
            htmlFor="location"
            className={`block text-sm font-medium ${theme.text.primary} mb-1`}
          >
            Location
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location || ""}
            onChange={handleChange}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
            placeholder="City, Country"
          />
        </div>

        {/* <div>
          <label
            htmlFor="summary"
            className={`block text-sm font-medium ${theme.text.primary} mb-1`}
          >
            Professional Summary
          </label>
          <textarea
            id="summary"
            name="summary"
            value={formData.summary || ""}
            onChange={handleChange}
            rows={2}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
            placeholder="Brief professional summary (max 160 characters)"
            maxLength={160}
          />
          <div className={`text-xs ${theme.text.secondary} mt-1`}>
            {formData.summary ? formData.summary.length : 0}/160 characters
          </div>
        </div> */}

        <div>
          <label
            htmlFor="aboutMe"
            className={`block text-sm font-medium ${theme.text.primary} mb-1`}
          >
            About Me
          </label>
          <textarea
            id="aboutMe"
            name="aboutMe"
            value={formData.aboutMe || ""}
            onChange={handleChange}
            rows={4}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
            placeholder="Tell us about yourself, your experience, and your interests"
          />
        </div>

        <div className="flex items-center justify-between pt-4">
          <Button
            type="button"
            onClick={() => navigate("/onboarding-2")}
            variant="secondary"
          >
            ← Back
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={loading || imageLoading}
          >
            {loading
              ? "Saving..."
              : imageLoading
              ? "Uploading Image..."
              : "Next Step →"}
          </Button>
        </div>
      </form>
    </OnboardingLayout>
  );
};

export default Onboarding03Page;
